import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useA11ySelector } from '@sg/a11y-helpers';
import { BalanceCard, IllusCredit_2, theme, useResponsive } from '@sg/garnish';

import { GiftCard } from '@order/features/gift-card';

import {
  GiftCardAccountSectionAddCardCta,
  GiftCardAccountSectionContainer,
  GiftCardAccountSectionGiftCardTotal,
} from './components';
import { useGiftCardBalance } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardAccountSection = (props: GiftCardAccountSectionProps) => {
  const { giftCardCode, giftCardRegistrationCode } = props;

  const { minWidth } = useResponsive();
  const { balance, isFetching } = useGiftCardBalance();

  const containerA11ySelector = useA11ySelector('account.payments.gift-card-container'); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  if (minWidth.isSM) {
    return (
      <GiftCardAccountSectionSM
        giftCardCode={giftCardCode}
        giftCardRegistrationCode={giftCardRegistrationCode}
        giftCardBalance={balance}
        isFetching={isFetching}
        {...containerA11ySelector.props}
      />
    );
  }

  return (
    <GiftCardAccountSectionXS
      giftCardBalance={balance}
      isFetching={isFetching}
      {...containerA11ySelector.props}
    />
  );
};

// ─── Responsive Components ───────────────────────────────────────────────────

export const GiftCardAccountSectionXS = (
  props: Omit<
    GiftCardAccountSectionInnerProps,
    'giftCardCode' | 'giftCardRegistrationCode'
  >,
) => {
  const { isFetching, giftCardBalance, dataSet } = props;

  return (
    <GiftCardAccountSectionContainer dataSet={dataSet}>
      <GiftCardAccountSectionGiftCardTotal
        totalAmount={giftCardBalance}
        isFetching={isFetching}
      />
      <GiftCardAccountSectionAddCardCta />
    </GiftCardAccountSectionContainer>
  );
};

export const GiftCardAccountSectionSM = (
  props: GiftCardAccountSectionInnerProps,
) => {
  const {
    giftCardCode,
    giftCardRegistrationCode,
    isFetching,
    giftCardBalance,
    dataSet,
  } = props;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <GiftCardAccountSectionContainer dataSet={dataSet}>
      <View style={styles.cardContainer}>
        <BalanceCard
          title={formatMessage(messages.accountGiftCardsGiftCardTotal)}
          balance={giftCardBalance}
          illustrationSource={IllusCredit_2}
          isLoading={isFetching}
        />
      </View>

      <View style={styles.formContainer}>
        <GiftCard.RedemptionForm
          style={styles.withoutBorder}
          giftCardCode={giftCardCode}
          giftCardRegistrationCode={giftCardRegistrationCode}
        />
      </View>
    </GiftCardAccountSectionContainer>
  );
};

//

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountGiftCardsGiftCardTotal: {
    defaultMessage: 'Gift card total',
    description: 'Account | Gift Cards | Gift Card Total',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardContainer: {
    alignItems: 'center',
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['12'],
  },
  formContainer: {
    paddingBottom: theme.spacing['6'],
  },
  withoutBorder: {
    padding: 0,
    borderWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardAccountSectionProps = {
  giftCardCode?: string;
  giftCardRegistrationCode?: string;
};

type GiftCardAccountSectionInnerProps = {
  giftCardBalance: string;
  isFetching: boolean;
  giftCardCode?: string;
  giftCardRegistrationCode?: string;
  dataSet?: Record<string, string>;
};
